<script setup>
import { budgetFlexibilityLabel } from '@/utils/inquiryCopy'
// import moment from 'moment'
import moment from 'moment-timezone'
import { mediaUrl } from '@core/utils/mediaUrl'
import axios from '@axios'
import {useReservationsStore, useRestaurantsStore, useUserGroupsStore, useUsersStore, useVarsStore} from "@/stores"
import { numberWithCommas, simulation, encodeUUID, restaurantUrlSegment, displayPhone } from '@/plugins/misc'
import { responsePageLinks } from '@/utils/responsePageLinks'
import { adminDeleteConfirmation, canDeleteReservation } from '@/utils/reservationDelete'
import socialRmLogo from "@/assets/social-rm.svg"
import MemberProfileCard from '@core/components/MemberProfileCard.vue'
import PreferredMemberDialog from '@core/components/PreferredMemberDialog.vue'

const props = defineProps({
  reservation: {
    type: Object,
    default: null
  },
  showResponseLinks: {
    type: Boolean,
    default: true
  },

  // Optional pre-fetched fan-card data from a parent batch lookup. When
  // provided, the card hydrates from this prop and skips its per-card fetch.
  // Shape: { favoritedRestaurants, bookingHistory, preferredAt } or
  // { accessDenied: true } when caller lacks scope.
  fanData: {
    type: Object,
    default: null
  },

  // When true, the parent guarantees fan-card data via `fanData` (typically
  // through a batched lookup). The card must not fire its own per-card fetch
  // even if `fanData` is briefly null while the batch is in flight; it just
  // waits for the prop to arrive. Required to keep large reservation lists
  // (e.g. the admin reservations dashboard) from flooding the network with
  // one request per card.
  batched: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['update:modelValue'])
const usersStore = useUsersStore()
let myUser = null;
({ myUser } = storeToRefs(usersStore))

const reservationsStore = useReservationsStore()
const varsStore = useVarsStore()

const userGroupsStore = useUserGroupsStore()
let restaurantUserGroups = null;
({  restaurantUserGroups  } = storeToRefs(userGroupsStore))

const restaurantsStore = useRestaurantsStore()
let viewingDetailsRestaurant = null
let viewingDetailsRestaurantTab = null
let restaurants = null;
({viewingDetailsRestaurant, viewingDetailsRestaurantTab, restaurants} = storeToRefs(restaurantsStore))

const isConfirmDialogOpen = ref(false)
// A delete the server refused (409): shown once, on this card only.
const deleteRefusalMessage = computed(() => {
  const r = reservationsStore.deleteRefusal
  
  return r && theReservation.value && r.id === theReservation.value._id ? r.message : null
})
const isDeleteRefusalOpen = computed({
  get: () => !!deleteRefusalMessage.value,
  set: open => { if (!open) reservationsStore.deleteRefusal = null },
})
const isConfirmResetDialogOpen = ref(false)

// Expandable full member profile (lazy-fetched on first open).
const profileExpanded = ref(false)
const fullMember = ref(null)
const loadingFullMember = ref(false)

async function toggleProfileExpanded() {
  profileExpanded.value = !profileExpanded.value
  const ownerId = props.reservation?.owner?._id
  if (profileExpanded.value && !fullMember.value && ownerId) {
    loadingFullMember.value = true
    try {
      // Don't trust fetchUserById's return value — when the user isn't already
      // cached it returns the last-pushed entry (which can be a follower from
      // the populated membersFollowed/membersWhoAreFollowing arrays, not the
      // requested user). Always re-lookup by id from the store after await.
      await usersStore.fetchUserById(ownerId)
      fullMember.value = usersStore.users.find(u => String(u._id) === String(ownerId)) || null
    } finally {
      loadingFullMember.value = false
    }
  }
}

// "Fan of" + "Bookings via RM" — both eager-fetched from the same endpoint so
// the count + favorite list can render in the collapsed label; panel contents
// reuse the cached history when the user expands.
const bookingsExpanded = ref(false)
const bookingHistory = ref(null)
const favoritedRestaurants = ref(null) // [{ _id, name, profileId }]
const preferredAt = ref([]) // [{ preferredId, scope, restaurantId?, userGroupId? }]
const loadingBookings = ref(false)
const bookingsError = ref(null)
const fanDataAccessDenied = ref(false) // caller has no relationship to this member → hide both lines
const expandedBookingRestaurants = reactive({}) // { [restaurantKey]: boolean }

// Preferred-status dialog state
const preferredDialogOpen = ref(false)
const isOwnerPreferred = computed(() => Array.isArray(preferredAt.value) && preferredAt.value.length > 0)

function openPreferredDialog() {
  preferredDialogOpen.value = true
}

function onPreferredUpdated({ isPreferred, restaurantIds, groupIds }) {
  // Rebuild a minimal preferredAt array from the dialog's emitted payload so the
  // button label updates immediately without re-fetching fan data.
  const next = []
  for (const id of restaurantIds || []) {
    next.push({ scope: 'restaurant', restaurantId: id })
  }
  for (const id of groupIds || []) {
    next.push({ scope: 'group', userGroupId: id })
  }
  preferredAt.value = next

  // Guard against any drift between records and isPreferred.
  if (!isPreferred) preferredAt.value = []
}

async function fetchFanData() {
  const ownerId = props.reservation?.owner?._id
  if (!ownerId || bookingHistory.value || loadingBookings.value) return

  // Platform admins don't see the fan-of / bookings-via-RM block, so skip the
  // request entirely.
  if (myUser.value?.userLevel === 'admin') return
  loadingBookings.value = true
  bookingsError.value = null
  try {
    const { data } = await axios.get(`/restaurant-admin/fans/${ownerId}`)
    bookingHistory.value = Array.isArray(data?.bookingHistory) ? data.bookingHistory : []
    favoritedRestaurants.value = Array.isArray(data?.favoritedRestaurants) ? data.favoritedRestaurants : []
    preferredAt.value = Array.isArray(data?.preferredAt) ? data.preferredAt : []
  } catch (err) {
    if (err?.response?.status === 403) {
      fanDataAccessDenied.value = true
    } else {
      bookingsError.value = err?.response?.data?.error || 'Failed to load bookings'
    }
  } finally {
    loadingBookings.value = false
  }
}

async function toggleBookingsExpanded() {
  bookingsExpanded.value = !bookingsExpanded.value
  if (bookingsExpanded.value) await fetchFanData()
}

function hydrateFromFanData(data) {
  if (!data) return false
  if (data.accessDenied) {
    fanDataAccessDenied.value = true
    
    return true
  }
  bookingHistory.value = Array.isArray(data.bookingHistory) ? data.bookingHistory : []
  favoritedRestaurants.value = Array.isArray(data.favoritedRestaurants) ? data.favoritedRestaurants : []
  preferredAt.value = Array.isArray(data.preferredAt) ? data.preferredAt : []
  
  return true
}

// Hydrate from the parent-provided batch result when available; only fall back
// to the per-card fetch when no batch data arrives (e.g. usages outside the
// reservations-review page that don't pre-fetch).
watch(
  () => props.fanData,
  data => { hydrateFromFanData(data) },
  { immediate: true },
)

onMounted(() => {
  if (bookingHistory.value || fanDataAccessDenied.value) return
  if (props.fanData) {
    hydrateFromFanData(props.fanData)
    
    return
  }

  // Parent batches this lookup — wait for the prop to arrive instead of firing
  // a redundant per-card request that would race the batch.
  if (props.batched) return
  fetchFanData()
})

const bookingCount = computed(() => (bookingHistory.value || []).length)

const fanOfLine = computed(() => {
  const favs = favoritedRestaurants.value
  if (!Array.isArray(favs) || favs.length === 0) return ''
  const names = favs.map(r => r?.name).filter(Boolean)
  if (names.length === 0) return ''

  return `Fan of: ${names.join(', ')}`
})

function toggleBookingRestaurant(key) {
  expandedBookingRestaurants[key] = !expandedBookingRestaurants[key]
}

function bookingSpend(res) {
  const perPerson = Number(res?.minSpendPerPerson) || 0
  const party = Number(res?.partySize) || 0

  return perPerson * party
}

function formatBookingCurrency(val) {
  if (!val) return '$0'

  return '$' + numberWithCommas(Math.round(val))
}

const bookingInsights = computed(() => {
  const history = bookingHistory.value || []
  const byRestaurant = new Map()
  let totalSpend = 0

  for (const res of history) {
    const name = res.confirmedRestaurant || res.preferredRestaurant || 'Unknown'
    const market = res.market || ''
    const key = `${name}||${market}`
    const spend = bookingSpend(res)

    totalSpend += spend
    if (!byRestaurant.has(key)) {
      byRestaurant.set(key, {
        key,
        restaurantName: name,
        market,
        bookings: [],
        bookingCount: 0,
        totalSpend: 0,
      })
    }
    const entry = byRestaurant.get(key)

    entry.bookings.push({ date: res.date, partySize: res.partySize, spend, status: res.status })
    entry.bookingCount += 1
    entry.totalSpend += spend
  }

  const restaurants = [...byRestaurant.values()].sort((a, b) => b.bookingCount - a.bookingCount)

  restaurants.forEach(r => r.bookings.sort((a, b) => moment(b.date).diff(moment(a.date))))

  return {
    total: history.length,
    totalSpend,
    byRestaurant: restaurants,
  }
})

const myRestaurants = computed(() => {
  //iterate through restaurantUserGroups and find where restaurantUserGroups.restaurant.name matches something in theReservation.value.requestedRestaurantsArray and restaurantUserGroups.restaurant.market == theReservation.value.market
  if (!theReservation.value?.requestedRestaurantsArray) return []
  let myRestaurantsToReturn = []
  for(let restaurant of restaurantUserGroups.value){
    if(theReservation.value.requestedRestaurantsArray.includes(restaurant.restaurant.name) && restaurant.restaurant.market == theReservation.value.market){
      myRestaurantsToReturn.push(restaurant.restaurant.name)
    }
  }

  return myRestaurantsToReturn
})

const theReservation = computed({
  get () {
    return props.reservation
  },

  set (value) {
    return emit('update:modelValue', value)
  }
})

// RM-400 — RM approves a member-reported group/private dining booking: Accepted at that restaurant.
const approvingBooking = ref(false)
const approveError = ref('')
async function approveBooking() {
  approvingBooking.value = true
  approveError.value = ''
  try {
    await reservationsStore.approveInquiryBooking(theReservation.value._id)
  } catch (err) {
    approveError.value = err?.response?.data?.message || 'Could not approve the booking. Please try again.'
  } finally {
    approvingBooking.value = false
  }
}

// RM-399 (10/21): a group/private inquiry's sent status is Submitted, never "Awaiting Response from
// Restaurant" (RM-400), so RM's reset on an inquiry goes back to Submitted - until now an inquiry left at
// Member Action could only be reset to Awaiting, the wrong status for it (Kapil's 10/21 test).
const resetStatus = computed(() => (theReservation.value?.reservationType === 'private' ? 'Submitted' : 'Awaiting Response from Restaurant'))
const resetLabel = computed(() => (resetStatus.value === 'Submitted' ? 'Submitted' : 'Awaiting Response'))

const menuItemsOptions = computed(() => {
  let menuItemsToReturn = [
    { title: 'View Details', action: function (){reservationsStore.viewReservation(theReservation.value._id)} },
  ]
  if(myUser?.value?.userLevel == 'admin'){
    menuItemsToReturn.push({ title: 'User Simulation', action: function (){simulation(theReservation.value?.owner?.email)} })
  }
  // RM-359: a request waiting for RM approval is decided on the review page
  // (approve / needs changes / decline), never by setting a status.
  if(myUser?.value?.userLevel == 'admin' && theReservation.value.status == "Pending RM Review"){
    menuItemsToReturn.push({
      title:"Review for approval",
      action: function(){window.location.assign('/admin-approvals')}})
  }
  if(theReservation.value.status == "Submitted"){
    menuItemsToReturn.push({
      title:"Set Awaiting Restaurant Response",
      action: function(){reservationsStore.setReservationStatus(theReservation.value._id, 'Awaiting Response from Restaurant')}})
  }

  // if(theReservation.value.status == "Awaiting Response from Restaurant" ){
  //   menuItemsToReturn.push({
  //     title:"Set Accepted",
  //     action: function(){reservationsStore.setReservationStatus(theReservation.value._id, 'Accepted')}})
  // }
  // if(theReservation.value.status == "Awaiting Response from Restaurant" ){
  //   menuItemsToReturn.push({
  //     title:"Set Not Available",
  //     action: function(){reservationsStore.setReservationStatus(theReservation.value._id, 'Not Available')}})
  // }
  // RM-319: a team's rows carry only its own restaurants' codes (null for the others)
  for(const link of responsePageLinks(theReservation.value, { isAdmin: myUser?.value?.userLevel == 'admin', myRestaurants: myRestaurants.value })){
    menuItemsToReturn.push({
      title:"Response Page - " + link.name,
      action: function(){window.open( `/referral/${restaurantUrlSegment('', link.name)}/${encodeUUID(link.code)}`, '_blank')}})
  }
  if(myUser?.value?.userLevel == 'admin' && (theReservation.value.status == "Submitted" || theReservation.value.status == "Awaiting Response from Restaurant" || theReservation.value.status == "Pending RM Review" )){
    menuItemsToReturn.push({
      title:"Set Rescinded",
      action: function(){reservationsStore.setReservationStatus(theReservation.value._id, 'Rescinded')}})
  }
  if(theReservation.value.status == "Canceling" ){
    menuItemsToReturn.push({
      title:"Set Canceled",
      action: function(){reservationsStore.setReservationStatus(theReservation.value._id, 'Canceled')}})
  }

  // if(theReservation.value.status == "Rescinding" ){
  //   menuItemsToReturn.push({
  //     title:"Set Rescinded",
  //     action: function(){reservationsStore.setReservationStatus(theReservation.value._id, 'Rescinded')}})
  // }
  if(myUser?.value?.userLevel == 'admin' && (theReservation.value.status != "Draft" && theReservation.value.status != "Submitted"  && theReservation.value.status != "Awaiting Response from Restaurant" && theReservation.value.status != "Pending RM Review")){
    menuItemsToReturn.push({
      title:"Reset to " + resetLabel.value,
      action: function(){isConfirmResetDialogOpen.value = true}})
  }
  if(myUser?.value?.userLevel == 'admin'){
    menuItemsToReturn.push({
      title:"Edit",
      action: function(){reservationsStore.editReservation(theReservation.value._id)}})
  }
  if(myUser?.value?.userLevel == 'admin'){
    menuItemsToReturn.push({
      title:"Edit Record",
      action: function(){varsStore.adminEditingReservation = theReservation.value}})
  }
  // RM Team deletes any status (Kapil 11/1): the server releases the card
  // hold first and refuses the delete if Stripe does not confirm it; a charged
  // hold is never deleted. Members' own Delete stays draft-only (RM-439).
  if(myUser?.value?.userLevel == 'admin' && canDeleteReservation(theReservation.value, { admin: true })){
    menuItemsToReturn.push({
      title:"Delete",
      action: function(){isConfirmDialogOpen.value = true}})
  }
  
  return menuItemsToReturn
})

const menuItemsStatus = computed(() => {
  // RM-359: the server refuses a raw status change into or out of Approval
  // Needed, so none is offered (use the review page).
  if (theReservation.value?.status === "Pending RM Review") return []
  let statuses = ["Draft","Submitted","Awaiting Response from Restaurant","In Review","Member Action","Accepted","Not Available","No Response","Declined","Canceled","Rescinded"]
  let menuItemsToReturn = []

  for(let status of statuses){
    menuItemsToReturn.push({
      title:"Set " + status,
      action: function(){reservationsStore.setReservationStatus(theReservation.value._id, status)}})
  }
  
  return menuItemsToReturn
})

const reservationTypeUiColors = {
  Draft: 'primary',
  Submitted: 'primary',
  'Approve?': 'warning',
  Accepted: 'success',
  Processing: 'secondary',
  "Awaiting Response from Restaurant": 'secondary',
  "In Review": 'secondary',
  "Member Action": 'secondary',
  "Pending RM Review": 'info',
  "Not Available": 'warning',
  "No Response": 'warning',
  "Declined": 'warning',
  "auto_declined": 'error',
}

const memberInitials = computed(() => {
  const owner = theReservation.value?.owner
  if (!owner) return '?'
  const first = owner.firstName?.[0]?.toUpperCase() || ''
  const last = owner.lastName?.[0]?.toUpperCase() || ''

  return (first + last) || '?'
})

// Mirror RestaurantHeart.vue showPlatinumBadge exactly so the signal is consistent.
const showPlatinumBadge = computed(() => {
  const owner = theReservation.value?.owner

  return owner?.isPlatinum
    || owner?.membershipType === 'Platinum'
    || owner?.membershipType === 'Trial'
})

const memberSubtitle = computed(() => {
  const owner = theReservation.value?.owner
  if (!owner) return ''

  return [owner.title, owner.company].filter(Boolean).join(' · ')
})

const statusLabel = computed(() => {
  const s = theReservation.value?.status
  if (s === 'Awaiting Response from Restaurant') return 'Awaiting Response'
  if (s === 'Accepted' && theReservation.value?.reservationType === 'instant') return 'Booked'

  return s || ''
})

const statusColor = computed(() => reservationTypeUiColors[theReservation.value?.status])

const tzAbbr = computed(() => theReservation.value?.timeZone?.abbreviation || '')
const tzId = computed(() => theReservation.value?.timeZone?.identifier)

function fmt(dt, pattern) {
  if (!dt || !tzId.value) return ''

  return moment(dt).tz(tzId.value).format(pattern)
}

const spendLine = computed(() => {
  const r = theReservation.value
  if (!r?.minSpendPerPerson || !r?.partySize) return null
  const total = numberWithCommas(r.minSpendPerPerson * r.partySize)
  const label = (r.reservationType === 'general' || r.reservationType === 'instant') ? 'min' : 'budget'

  return `$${total} F&B ${label} ($${numberWithCommas(r.minSpendPerPerson)}pp)`
})

// Hold is only relevant while the reservation is actively in-flight or
// upcoming. Hide it for terminal / past states (Submitted, Experienced,
// Not Available, No Response, Declined, Rescinded, Canceled, Draft).
const HOLD_VISIBLE_STATUSES = new Set([
  'Pending RM Review', // RM-359: the hold is placed at submit and stays while RM reviews
  'Awaiting Response from Restaurant',
  'In Review',
  'Member Action',
])

const shouldShowHold = computed(() => {
  const r = theReservation.value
  if (!r) return false

  return HOLD_VISIBLE_STATUSES.has(r.status)
    || (r.status === 'Accepted' && moment(r.sortDateTime).isAfter(moment()))
})

const holdLine = computed(() => {
  if (!shouldShowHold.value) return null
  const r = theReservation.value
  if (!r) return null
  if (r.stripeHoldCapturedAmount) return { label: 'Charged', value: `$${numberWithCommas(r.stripeHoldCapturedAmount)}`, tone: 'neutral' }
  if (r.stripeHoldReleasedAt) return { label: 'Hold', value: 'Released', tone: 'neutral' }
  if (r.stripeHoldConfirmedAmount) return { label: 'On hold', value: `$${numberWithCommas(r.stripeHoldConfirmedAmount)}`, tone: 'info' }
  if (r.status !== 'Accepted' && r.stripeHoldMaxAmount) return { label: 'Hold up to', value: `$${numberWithCommas(r.stripeHoldMaxAmount)}`, tone: 'neutral' }
  if (r.status === 'Accepted' && r.stripeHoldConfirmedAmount === 0) return { label: 'Hold', value: 'Waived', tone: 'muted' }

  return null
})

const showRestaurantDetails = (marketName, restaurantName) => {
  console.log('showRestaurantDetails', marketName, restaurantName)

  //find the restaurant
  let restaurant = restaurants.value.find(r => r.name == restaurantName && r.market == marketName)
  if (restaurant){
    viewingDetailsRestaurantTab.value = "Details"
    viewingDetailsRestaurant.value = restaurant
  }
}

const openNewTab = url => {
  window.open(url, '_blank')
}
</script>

<template>
  <article
    v-if="theReservation && theReservation._id"
    class="rcadmin"
  >
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogOpen"
      :confirmation-msg="adminDeleteConfirmation(theReservation)"
      @confirm="reservationsStore.deleteReservation(theReservation._id)"
    />
    <VSnackbar
      v-model="isDeleteRefusalOpen"
      color="error"
      :timeout="12000"
      location="bottom"
    >
      <span class="text-body-1">Not deleted. {{ deleteRefusalMessage }}</span>
    </VSnackbar>
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmResetDialogOpen"
      :confirmation-msg="`Reset to '${resetLabel}'?`"
      @confirm="reservationsStore.setReservationStatus(theReservation._id, resetStatus)"
    />

    <!-- Header: restaurant + status + admin menu -->
    <header class="rcadmin__header">
      <div class="rcadmin__title-group">
        <h3 class="rcadmin__title">
          <!-- RM-400: a restaurant's own entry for a group/private dining inquiry -->
          <button
            v-if="theReservation?._displayRestaurantName"
            type="button"
            class="rcadmin__title-link"
            @click="showRestaurantDetails(theReservation.market, theReservation._displayRestaurantName)"
          >
            {{ theReservation._displayRestaurantName }}
          </button>
          <button
            v-else-if="theReservation?.confirmedRestaurant"
            type="button"
            class="rcadmin__title-link"
            @click="showRestaurantDetails(theReservation.market, theReservation.confirmedRestaurant)"
          >
            {{ theReservation.confirmedRestaurant }}
          </button>
          <template v-else-if="theReservation?.requestedRestaurantsArray">
            <template
              v-for="(restaurant, index) in theReservation.requestedRestaurantsArray"
              :key="restaurant + index"
            >
              <button
                type="button"
                class="rcadmin__title-link"
                @click="showRestaurantDetails(theReservation.market, restaurant)"
              >
                {{ restaurant }}
              </button><span
                v-if="index < theReservation.requestedRestaurantsArray.length - 1"
                class="rcadmin__title-sep"
              >,&nbsp;</span>
            </template>
          </template>
        </h3>

        <!-- Metadata strip: date + time lead; party + spend follow -->
        <div class="rcadmin__meta">
          <div class="rcadmin__meta-primary">
            <span class="rcadmin__meta-item rcadmin__meta-item--primary">
              <VIcon
                size="18"
                class="rcadmin__meta-icon"
              >
                mdi-calendar-outline
              </VIcon>
              {{ theReservation.date ? moment(theReservation.date).format('ddd, M/D') : 'No date' }}
            </span>
            <span class="rcadmin__meta-item rcadmin__meta-item--primary">
              <VIcon
                size="18"
                class="rcadmin__meta-icon"
              >
                mdi-clock-outline
              </VIcon>
              {{ moment(theReservation.confirmedTime || theReservation.preferredTime, ['HH:mm', 'H:mm', 'h:mm A', 'h:mmA']).format('h:mm A') }}<span
                v-if="tzAbbr"
                class="rcadmin__meta-sub"
              > {{ tzAbbr }}</span>
            </span>
          </div>
          <div class="rcadmin__meta-secondary">
            <span class="rcadmin__meta-item">
              <VIcon
                size="16"
                class="rcadmin__meta-icon"
              >
                mdi-account-multiple-outline
              </VIcon>
              Party of {{ theReservation.partySize }}
            </span>
            <span
              v-if="spendLine"
              class="rcadmin__meta-item"
            >
              <VIcon
                size="16"
                class="rcadmin__meta-icon"
              >
                mdi-cash
              </VIcon>
              {{ spendLine }}
            </span>
          </div>
        </div>

        <!-- RM-400: a group/private dining inquiry's event details and, for its own entry, where else it went -->
        <div
          v-if="theReservation.reservationType === 'private'"
          class="rcadmin__inquiry"
        >
          <div class="rcadmin__inquiry-line">
            Group/private dining inquiry<template v-if="theReservation.roomPreference">
              · {{ theReservation.roomPreference }}
            </template><template v-if="theReservation.seatingPreference">
              · {{ theReservation.seatingPreference }}
            </template><template v-if="theReservation.budgetFlexibility">
              · {{ budgetFlexibilityLabel(theReservation.budgetFlexibility, theReservation.minSpendPerPerson) }}
            </template>
          </div>
          <div
            v-if="theReservation.specialRequests"
            class="rcadmin__inquiry-line"
          >
            Notes / Requests: {{ theReservation.specialRequests }}<template v-if="theReservation.specialRequestFlexibility">
              ({{ theReservation.specialRequestFlexibility }})
            </template>
          </div>
          <div
            v-if="theReservation._alsoSentTo?.length"
            class="rcadmin__inquiry-line"
          >
            Also sent to {{ theReservation._alsoSentTo.join(', ') }}
          </div>
          <div
            v-if="myUser?.userLevel === 'admin' && theReservation.status === 'Approve?'"
            class="rcadmin__approve"
          >
            <div class="rcadmin__inquiry-line">
              Booked at <strong>{{ theReservation.confirmedRestaurant }}</strong> at {{ theReservation.confirmedTime }}<template v-if="theReservation.bookedReportedBy?.firstName">
                · reported by {{ theReservation.bookedReportedBy.firstName }} {{ theReservation.bookedReportedBy.lastName }}
              </template>
            </div>
            <div
              v-for="mediaId in (theReservation.bookedProofMediaIds || [])"
              :key="mediaId"
              class="rcadmin__inquiry-line"
            >
              <a
                :href="mediaUrl(mediaId, 'original')"
                target="_blank"
                rel="noopener noreferrer"
                class="rcadmin__footer-link"
              >
                <VIcon
                  start
                  size="14"
                >
                  mdi-file-document-outline
                </VIcon>
                Contract or receipt
              </a>
            </div>
            <VBtn
              color="primary-dark"
              size="small"
              rounded="pill"
              class="rcadmin__approve-btn"
              :loading="approvingBooking"
              @click.stop="approveBooking"
            >
              Approve booking
            </VBtn>
            <div
              v-if="approveError"
              class="rcadmin__inquiry-line rcadmin__inquiry-error"
            >
              {{ approveError }}
            </div>
          </div>
        </div>
        <!-- Restaurant admin response links (moved up from footer) -->
        <div
          v-if="props.showResponseLinks && myUser?.userLevel !== 'admin' && theReservation.requestedRestaurantsArray"
          class="rcadmin__response-links"
        >
          <template
            v-for="(restaurantName, index) in theReservation.requestedRestaurantsArray"
            :key="`response-${index}`"
          >
            <a
              v-if="myRestaurants.includes(restaurantName) && theReservation.confirmationUuids?.[index]"
              :href="`/referral/${restaurantUrlSegment('', restaurantName)}/${encodeUUID(theReservation.confirmationUuids[index])}`"
              target="_blank"
              rel="noopener noreferrer"
              class="rcadmin__footer-link"
            >
              <VIcon
                start
                size="14"
              >
                mdi-open-in-new
              </VIcon>
              Open response page — {{ restaurantName }}
            </a>
          </template>
        </div>

        <!-- Accepted / Booked timestamp (moved up from timestamps block) -->
        <div
          v-if="theReservation.acceptedAt"
          class="rcadmin__accepted-line"
        >
          {{ theReservation.reservationType === 'instant' ? 'Booked' : 'Accepted' }}
          {{ fmt(theReservation.acceptedAt, 'M/DD/YY [at] h:mma') }} {{ tzAbbr }}<span
            v-if="theReservation.confirmedByName"
          > by {{ theReservation.confirmedByName }}</span>
        </div>

        <div
          v-if="theReservation.owner && !profileExpanded"
          class="rcadmin__member"
        >
          <component
            :is="theReservation.owner.profileId ? 'a' : 'div'"
            v-bind="theReservation.owner.profileId ? {
              href: `/m/${theReservation.owner.profileId}`,
              target: '_blank',
              rel: 'noopener noreferrer',
              'aria-label': 'View member profile',
            } : {}"
            class="rcadmin__avatar-slot"
          >
            <VAvatar
              v-if="theReservation.owner.profileImageId"
              :size="36"
              class="rcadmin__avatar"
            >
              <VImg
                :src="mediaUrl(theReservation.owner.profileImageId, 'thumb')"
                :alt="theReservation.owner.firstName"
              />
            </VAvatar>
            <VAvatar
              v-else
              :size="36"
              class="rcadmin__avatar rcadmin__avatar--initials"
            >
              <span class="rcadmin__avatar-text">{{ memberInitials }}</span>
            </VAvatar>
          </component>

          <div class="rcadmin__member-info">
            <div class="rcadmin__member-name-row">
              <a
                v-if="theReservation.owner.profileId"
                :href="`/m/${theReservation.owner.profileId}`"
                target="_blank"
                rel="noopener noreferrer"
                class="rcadmin__member-link"
              >{{ theReservation.owner.fullName }}</a>
              <span
                v-else
                class="rcadmin__member-name-static"
              >{{ theReservation.owner.fullName }}</span>
              <span
                v-if="theReservation.owner.verified || showPlatinumBadge"
                class="rcadmin__member-badges"
              >
                <VIcon
                  v-if="theReservation.owner.verified"
                  size="14"
                  class="rcadmin__member-verified"
                  aria-label="Verified member"
                >
                  material-symbols:verified
                </VIcon>
                <span
                  v-if="showPlatinumBadge"
                  class="rcadmin__platinum-label"
                >
                  (Platinum)
                </span>
              </span>
            </div>
            <div
              v-if="memberSubtitle"
              class="rcadmin__member-subtitle"
            >
              {{ memberSubtitle }}
            </div>
          </div>
        </div>
        <div
          v-else-if="!theReservation.owner"
          class="rcadmin__member rcadmin__member--missing"
        >
          Owner does not exist
        </div>

        <div
          v-if="theReservation.owner"
          class="rcadmin__member-actions"
        >
          <button
            type="button"
            class="rcadmin__profile-toggle"
            :aria-expanded="profileExpanded"
            @click="toggleProfileExpanded"
          >
            <span>{{ profileExpanded ? 'Hide profile' : 'View full profile' }}</span>
            <VIcon size="14">
              {{ profileExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </VIcon>
          </button>
          <button
            v-if="myUser?.userLevel !== 'admin' && !fanDataAccessDenied"
            type="button"
            class="rcadmin__preferred-toggle"
            :class="{ 'rcadmin__preferred-toggle--active': isOwnerPreferred }"
            :aria-label="isOwnerPreferred ? 'Manage Preferred status' : 'Mark as Preferred'"
            @click.stop="openPreferredDialog"
          >
            <VIcon size="14">
              {{ isOwnerPreferred ? 'mdi-star' : 'mdi-star-outline' }}
            </VIcon>
            <span>{{ isOwnerPreferred ? 'Preferred' : 'Mark Preferred' }}</span>
          </button>
        </div>
      </div>

      <div class="rcadmin__actions">
        <VMenu v-if="myUser?.userLevel === 'admin'">
          <template #activator="{ props: chipProps }">
            <VChip
              v-bind="chipProps"
              size="small"
              :color="statusColor"
              class="rcadmin__status rcadmin__status--clickable"
            >
              {{ statusLabel }}
            </VChip>
          </template>
          <VList density="compact">
            <VListItem>
              <VListItemTitle class="rcadmin__status-warning">
                <b>USE BELOW WITH CAUTION</b>
              </VListItemTitle>
            </VListItem>
            <VListItem
              v-for="(item, i) in menuItemsStatus"
              :key="i"
              @click="item.action()"
            >
              <VListItemTitle>{{ item.title }}</VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>
        <VChip
          v-else
          size="small"
          :color="statusColor"
          class="rcadmin__status"
        >
          {{ statusLabel }}
        </VChip>
        <VMenu v-if="myUser?.userLevel === 'admin'">
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              icon="mdi-dots-vertical"
              density="compact"
              variant="text"
              size="small"
              class="rcadmin__menu-btn"
              aria-label="Reservation options"
            />
          </template>
          <VList density="compact">
            <VListItem
              v-for="(item, i) in menuItemsOptions"
              :key="i"
              @click="item.action()"
            >
              <VListItemTitle>{{ item.title }}</VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>
      </div>
    </header>

    <!-- Expanded full profile (lazy-loaded, replaces compact member + contact blocks) -->
    <div
      v-if="profileExpanded && theReservation.owner"
      class="rcadmin__profile-expanded"
    >
      <div
        v-if="loadingFullMember && !fullMember"
        class="rcadmin__profile-loading"
      >
        <VProgressCircular
          indeterminate
          color="primary-dark"
          size="28"
        />
      </div>
      <MemberProfileCard
        v-else
        :member="fullMember || theReservation.owner"
      />
    </div>

    <!-- Member contact (hidden when full profile is expanded — MemberProfileCard includes them) -->
    <div
      v-if="!profileExpanded && (theReservation.owner?.phoneNumber || theReservation.owner?.email)"
      class="rcadmin__contact"
    >
      <a
        v-if="theReservation.owner?.phoneNumber"
        class="rcadmin__contact-item"
        :href="`tel:${theReservation.owner.phoneNumber}`"
      >
        <VIcon
          size="14"
          class="rcadmin__contact-icon"
        >
          mdi-phone-outline
        </VIcon>
        <span>{{ displayPhone(theReservation.owner.phoneNumber) }}</span>
      </a>
      <a
        v-if="theReservation.owner?.email"
        class="rcadmin__contact-item"
        :href="`mailto:${theReservation.owner.email}`"
      >
        <VIcon
          size="14"
          class="rcadmin__contact-icon"
        >
          mdi-email-outline
        </VIcon>
        <span>{{ theReservation.owner.email }}</span>
      </a>
    </div>

    <!--
      Fan of + Bookings via RM (eager fetch, inline expansion on click).
      Hidden for platform admins — they don't manage a specific restaurant
      scope, so the "fan of" / "bookings via RM" framing isn't meaningful. 
    -->
    <div
      v-if="theReservation.owner?._id && !fanDataAccessDenied && myUser?.userLevel !== 'admin'"
      class="rcadmin__bookings-wrapper"
    >
      <p
        v-if="fanOfLine"
        class="rcadmin__fan-of"
      >
        {{ fanOfLine }}
      </p>
      <button
        type="button"
        class="rcadmin__bookings-trigger"
        :aria-expanded="bookingsExpanded"
        aria-controls="rcadmin-bookings-panel"
        @click="toggleBookingsExpanded"
      >
        <span>Bookings via RM: {{ bookingHistory === null ? '…' : bookingCount }}</span>
        <VIcon size="14">
          {{ bookingsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </VIcon>
      </button>
      <VExpandTransition>
        <div
          v-if="bookingsExpanded"
          id="rcadmin-bookings-panel"
          class="rcadmin__bookings-panel"
        >
          <div
            v-if="loadingBookings"
            class="rcadmin__bookings-loading"
          >
            <VProgressCircular
              indeterminate
              size="20"
              color="primary-dark"
            />
          </div>
          <div
            v-else-if="bookingsError"
            class="rcadmin__bookings-error"
          >
            {{ bookingsError }}
          </div>
          <div
            v-else-if="bookingInsights.total === 0"
            class="rcadmin__bookings-empty"
          >
            No bookings yet.
          </div>
          <template v-else>
            <div class="rcadmin__bookings-summary">
              <div class="rcadmin__bookings-summary-stat">
                <div class="rcadmin__bookings-stat-value">
                  {{ bookingInsights.total }}
                </div>
                <div class="rcadmin__bookings-stat-label">
                  Bookings
                </div>
              </div>
              <div class="rcadmin__bookings-summary-stat">
                <div class="rcadmin__bookings-stat-value">
                  {{ formatBookingCurrency(bookingInsights.totalSpend) }}
                </div>
                <div class="rcadmin__bookings-stat-label">
                  Guaranteed F&amp;B Spend
                </div>
              </div>
            </div>
            <div
              v-for="r in bookingInsights.byRestaurant"
              :key="r.key"
              class="rcadmin__bookings-restaurant"
            >
              <button
                type="button"
                class="rcadmin__bookings-restaurant-header"
                :aria-expanded="Boolean(expandedBookingRestaurants[r.key])"
                @click="toggleBookingRestaurant(r.key)"
              >
                <div class="rcadmin__bookings-restaurant-info">
                  <div class="rcadmin__bookings-restaurant-name">
                    {{ r.restaurantName }}
                  </div>
                  <div
                    v-if="r.market"
                    class="rcadmin__bookings-restaurant-market"
                  >
                    {{ r.market }}
                  </div>
                </div>
                <div class="rcadmin__bookings-restaurant-totals">
                  <div class="rcadmin__bookings-restaurant-count">
                    {{ r.bookingCount }} {{ r.bookingCount === 1 ? 'booking' : 'bookings' }}
                  </div>
                  <div class="rcadmin__bookings-restaurant-spend">
                    {{ formatBookingCurrency(r.totalSpend) }}
                  </div>
                </div>
                <VIcon
                  size="16"
                  class="rcadmin__bookings-restaurant-chevron"
                >
                  {{ expandedBookingRestaurants[r.key] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </VIcon>
              </button>
              <VExpandTransition>
                <div
                  v-if="expandedBookingRestaurants[r.key]"
                  class="rcadmin__bookings-rows"
                >
                  <div
                    v-for="(b, idx) in r.bookings"
                    :key="idx"
                    class="rcadmin__bookings-row"
                  >
                    <span class="rcadmin__bookings-row-date">
                      {{ b.date ? moment(b.date).format('MM-DD-YY') : '—' }}
                    </span>
                    <span
                      v-if="b.partySize"
                      class="rcadmin__bookings-row-party"
                    >Party of {{ b.partySize }}</span>
                    <span
                      v-else
                      class="rcadmin__bookings-row-party"
                    />
                    <span class="rcadmin__bookings-row-spend">
                      {{ formatBookingCurrency(b.spend) }}
                    </span>
                  </div>
                </div>
              </VExpandTransition>
            </div>
          </template>
        </div>
      </VExpandTransition>
    </div>

    <!-- Stripe hold state -->
    <div
      v-if="holdLine"
      class="rcadmin__hold"
      :class="`rcadmin__hold--${holdLine.tone}`"
    >
      <span class="rcadmin__hold-label">{{ holdLine.label }}</span>
      <span class="rcadmin__hold-value">{{ holdLine.value }}</span>
    </div>

    <!-- Timestamps -->
    <div class="rcadmin__timestamps">
      <div v-if="theReservation.submittedAt && (theReservation.status === 'Submitted' || theReservation.status === 'Awaiting Response from Restaurant')">
        Submitted {{ fmt(theReservation.submittedAt, 'M/DD [at] h:mma') }} {{ tzAbbr }}
      </div>
      <div v-if="theReservation.submittedAt && theReservation.status === 'No Response'">
        Submitted {{ fmt(theReservation.submittedAt, 'M/DD/YY [at] h:mma') }} {{ tzAbbr }}
      </div>
      <div v-if="theReservation.declinedAt">
        Declined {{ fmt(theReservation.declinedAt, 'M/DD/YY [at] h:mma') }} {{ tzAbbr }}
      </div>
      <div
        v-if="['Awaiting Response from Restaurant','Submitted','In Review','Member Action'].includes(theReservation.status) && theReservation?.requestExpirationTime"
        class="rcadmin__timestamp-warn"
      >
        Expires {{ fmt(theReservation.requestExpirationTime, 'M/DD [at] h:mma') }} {{ tzAbbr }} ({{ moment(theReservation.requestExpirationTime).fromNow() }})
      </div>
      <div v-if="theReservation.status === 'No Response' && theReservation?.expiredAt">
        Expired {{ fmt(theReservation.expiredAt, 'M/DD/YY [at] h:mma') }} {{ tzAbbr }}
      </div>
    </div>


    <!-- Messages from the confirming restaurant -->
    <VAlert
      v-if="theReservation.confirmedMessageToRm"
      type="info"
      icon="mdi-message-text-outline"
      variant="tonal"
      density="compact"
      class="rcadmin__alert"
      :title="`Message from ${theReservation.confirmedRestaurant} to RM`"
    >
      <p class="rcadmin__alert-body">
        {{ theReservation.confirmedMessageToRm }}
      </p>
    </VAlert>

    <VAlert
      v-if="theReservation.confirmedMessageToMember"
      type="info"
      icon="mdi-message-text-outline"
      variant="tonal"
      density="compact"
      class="rcadmin__alert"
      :title="`Message from ${theReservation.confirmedRestaurant} to Member`"
    >
      <p class="rcadmin__alert-body">
        {{ theReservation.confirmedMessageToMember }}
      </p>
    </VAlert>

    <!-- Declines (chip + any accompanying messages) -->
    <template v-if="theReservation.declines && theReservation.declines.length === theReservation.requestedRestaurantsArray?.length">
      <template
        v-for="(decline, index) in theReservation.declines"
        :key="`decline-${index}`"
      >
        <VChip
          v-if="decline && !(decline.declineMessageToRm || decline.declineMessageToMember)"
          size="small"
          color="warning"
          variant="tonal"
          class="rcadmin__decline-chip"
        >
          <VIcon
            start
            size="14"
          >
            mdi-calendar-remove-outline
          </VIcon>
          Declined by {{ theReservation.requestedRestaurantsArray[index] }}
        </VChip>

        <VAlert
          v-if="decline && decline.declineMessageToMember"
          type="warning"
          icon="mdi-message-text-outline"
          variant="tonal"
          density="compact"
          class="rcadmin__alert"
          :title="`Message from ${theReservation.requestedRestaurantsArray[index]} to Member`"
        >
          <p class="rcadmin__alert-body">
            {{ decline.declineMessageToMember }}
          </p>
        </VAlert>

        <VAlert
          v-if="decline && decline.declineMessageToRm"
          type="warning"
          icon="mdi-message-text-outline"
          variant="tonal"
          density="compact"
          class="rcadmin__alert"
          :title="`Message from ${theReservation.requestedRestaurantsArray[index]} to RM`"
        >
          <p class="rcadmin__alert-body">
            {{ decline.declineMessageToRm }}
          </p>
        </VAlert>
      </template>
    </template>

    <PreferredMemberDialog
      v-if="theReservation.owner && myUser?.userLevel !== 'admin'"
      v-model="preferredDialogOpen"
      :member="theReservation.owner"
      @preferred-updated="onPreferredUpdated"
    />
  </article>
</template>

<style lang="scss" scoped>
.rcadmin {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-3, 12px);
  padding: var(--ds-space-4, 16px);
  background: var(--ds-surface-card, #fff);
  border: 1px solid var(--ds-border-subtle, #eaeaef);
  border-radius: var(--ds-radius-lg, 12px);
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.rcadmin:hover {
  border-color: var(--ds-border-default, #d9dbdd);
  box-shadow: var(--ds-shadow-sm);
}

/* ─── Header ──────────────────────────────────────────────────────────── */

.rcadmin__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
}

.rcadmin__title-group {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2, 8px);
}

.rcadmin__response-links {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-1, 4px);
}

.rcadmin__accepted-line {
  font-size: var(--ds-text-xs, 12px);
  color: var(--ds-text-muted);
  line-height: 1.5;
}

.rcadmin__title {
  margin: 0;
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-lg, 18px);
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: var(--ds-text-strong, #1a1a1a);
  word-break: break-word;
}

.rcadmin__title-link {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: color 150ms ease;
}

.rcadmin__title-link:hover,
.rcadmin__title-link:focus-visible {
  color: var(--ds-brand-primary-darker);
  text-decoration: underline;
  text-underline-offset: 2px;
  outline: none;
}

.rcadmin__title-sep {
  color: var(--ds-text-muted);
  font-weight: 400;
}

.rcadmin__member {
  display: flex;
  align-items: flex-start;
  gap: var(--ds-space-2, 8px);
  margin-top: var(--ds-space-2, 8px);
  font-size: var(--ds-text-sm, 13px);
  color: var(--ds-text-secondary, #595959);
}

.rcadmin__avatar-slot {
  display: flex;
  flex-shrink: 0;
  text-decoration: none;
  border-radius: 50%;
  transition: box-shadow 150ms ease;
}

a.rcadmin__avatar-slot:hover,
a.rcadmin__avatar-slot:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--ds-brand-primary-light), 0 0 0 3px var(--ds-brand-primary);
}

.rcadmin__avatar--initials {
  background: var(--ds-brand-primary-light, #e9f4f3);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 2px;
}

.rcadmin__avatar-text {
  font-size: var(--ds-text-sm);
  font-weight: 600;
  color: var(--ds-brand-primary-darker);
}

.rcadmin__member-info {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/*
 * Natural inline flow: the name text wraps through its own lines; the
 * verified-check + (Platinum) badge group is a single inline-flex element
 * that sits at the end of the flow and wraps to the next line together
 * when there isn't room. Name always appears first, badges never interleave.
 */
.rcadmin__member-name-row {
  line-height: 1.3;
  color: var(--ds-text-secondary);
  font-weight: 500;
  word-break: break-word;
}

.rcadmin__member-link,
.rcadmin__member-name-static {
  color: inherit;
  text-decoration: none;
  transition: color 150ms ease;
}

.rcadmin__member-badges {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
}

.rcadmin__member-link:hover,
.rcadmin__member-link:focus-visible {
  color: var(--ds-brand-primary-darker);
  text-decoration: underline;
  text-underline-offset: 2px;
  outline: none;
}

.rcadmin__member-verified {
  color: rgb(0, 149, 246);
  margin-top: -1px;
}

.rcadmin__platinum-label {
  font-size: var(--ds-text-xs, 12px);
  color: inherit;
}

.rcadmin__member-subtitle {
  font-size: var(--ds-text-xs, 12px);
  color: var(--ds-text-muted);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rcadmin__member--missing {
  font-style: italic;
  color: var(--ds-text-muted);
}

.rcadmin__member-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: var(--ds-space-2, 8px);
}

.rcadmin__profile-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px 4px 12px;
  background: transparent;
  border: 1px solid var(--ds-border-default);
  border-radius: 999px;
  font-size: var(--ds-text-xs, 12px);
  font-weight: 500;
  color: var(--ds-text-secondary);
  cursor: pointer;
  transition: all 150ms ease;
}

.rcadmin__profile-toggle:hover,
.rcadmin__profile-toggle:focus-visible {
  background: var(--ds-brand-primary-light);
  border-color: var(--ds-brand-primary);
  color: var(--ds-brand-primary-darker);
  outline: none;
}

.rcadmin__preferred-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px 4px 10px;
  background: transparent;
  border: 1px solid var(--ds-brand-primary);
  border-radius: 999px;
  font-size: var(--ds-text-xs, 12px);
  font-weight: 600;
  color: var(--ds-brand-primary-darker);
  cursor: pointer;
  transition: all 150ms ease;
}

.rcadmin__preferred-toggle:hover,
.rcadmin__preferred-toggle:focus-visible {
  background: var(--ds-brand-primary-light);
  outline: none;
}

.rcadmin__preferred-toggle--active {
  background: var(--ds-brand-primary-dark);
  border-color: var(--ds-brand-primary-dark);
  color: #fff;
}

.rcadmin__preferred-toggle--active:hover,
.rcadmin__preferred-toggle--active:focus-visible {
  background: var(--ds-brand-primary-darker);
  border-color: var(--ds-brand-primary-darker);
  color: #fff;
}

.rcadmin__profile-expanded {
  margin-top: var(--ds-space-2, 8px);
}

.rcadmin__profile-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-8, 32px) 0;
  background: var(--ds-surface-subtle);
  border-radius: var(--ds-radius-md, 12px);
}

.rcadmin__actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-1, 4px);
  flex-shrink: 0;
}

.rcadmin__status {
  font-weight: 600;
  letter-spacing: 0;
}

.rcadmin__status--clickable {
  cursor: pointer;
}

.rcadmin__status-warning {
  color: var(--ds-alert-warning-text);
  font-size: var(--ds-text-xs);
}

.rcadmin__menu-btn {
  margin-left: 2px;
}

/* ─── Metadata strip ──────────────────────────────────────────────────── */

.rcadmin__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--ds-text-primary, #3a3541);
}

.rcadmin__meta-primary {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--ds-space-1, 4px) var(--ds-space-4, 16px);
}

.rcadmin__meta-secondary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--ds-space-1, 4px) var(--ds-space-3, 12px);
  font-size: var(--ds-text-sm, 13px);
  color: var(--ds-text-secondary);
}

.rcadmin__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-variant-numeric: tabular-nums;
}

.rcadmin__meta-item--primary {
  font-size: var(--ds-text-md, 16px);
  font-weight: 600;
  color: var(--ds-text-strong);
  letter-spacing: -0.005em;
}

.rcadmin__meta-icon {
  color: var(--ds-text-muted);
  flex-shrink: 0;
}

.rcadmin__meta-item--primary .rcadmin__meta-icon {
  color: var(--ds-brand-primary-darker);
}

.rcadmin__meta-sub {
  color: var(--ds-text-muted);
  font-size: var(--ds-text-xs);
  font-weight: 400;
  margin-left: 2px;
}

/* ─── Stripe hold ─────────────────────────────────────────────────────── */

.rcadmin__hold {
  display: inline-flex;
  align-items: baseline;
  gap: var(--ds-space-2, 8px);
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--ds-surface-inset, #f4f4f6);
  font-size: var(--ds-text-xs, 12px);
  width: fit-content;
  max-width: 100%;
}

.rcadmin__hold-label {
  color: var(--ds-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.rcadmin__hold-value {
  color: var(--ds-text-strong);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  font-size: var(--ds-text-sm);
}

.rcadmin__hold--info {
  background: var(--ds-alert-info-bg);
}

.rcadmin__hold--info .rcadmin__hold-value {
  color: var(--ds-alert-info-text);
}

.rcadmin__hold--muted {
  background: transparent;
  border: 1px dashed var(--ds-border-default);
  opacity: 0.85;
}

/* ─── Timestamps ──────────────────────────────────────────────────────── */

.rcadmin__timestamps {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--ds-text-xs, 12px);
  color: var(--ds-text-muted);
  line-height: 1.5;
}

.rcadmin__timestamps:empty {
  display: none;
}

.rcadmin__timestamp-warn {
  color: var(--ds-alert-warning-text);
  font-weight: 500;
}

/* ─── Contact ─────────────────────────────────────────────────────────── */

.rcadmin__contact {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-space-1, 4px) var(--ds-space-3, 12px);
  padding: var(--ds-space-2, 8px) var(--ds-space-3, 12px);
  background: var(--ds-surface-subtle);
  border-radius: var(--ds-radius-md, 8px);
  font-size: var(--ds-text-xs, 12px);
}

.rcadmin__contact-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ds-text-secondary);
  text-decoration: none;
  transition: color 150ms ease;
}

.rcadmin__contact-item:hover {
  color: var(--ds-brand-primary-darker);
}

.rcadmin__contact-item--profile {
  color: var(--ds-brand-primary-darker);
  font-weight: 500;
}

.rcadmin__contact-logo {
  display: block;
}

.rcadmin__contact-icon {
  color: var(--ds-text-muted);
}

/* ─── Bookings via RM (inline expandable insights) ────────────────────── */

.rcadmin__bookings-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--ds-space-2, 8px);
}

.rcadmin__fan-of {
  margin: 0;
  font-size: var(--ds-text-xs, 12px);
  color: var(--ds-text-muted, #777);
  align-self: flex-start;
  word-break: break-word;
}

.rcadmin__bookings-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  padding: 2px 2px 2px 0;
  margin: 0;
  background: transparent;
  border: 0;
  border-radius: 4px;
  font-size: var(--ds-text-xs, 12px);
  font-weight: 500;
  color: var(--ds-brand-primary-darker, #0FA091);
  cursor: pointer;
  transition: background 150ms ease;
}

.rcadmin__bookings-trigger:hover,
.rcadmin__bookings-trigger:focus-visible {
  background: var(--ds-brand-primary-light, #e9f4f3);
  outline: none;
}

.rcadmin__bookings-panel {
  padding: var(--ds-space-3, 12px);
  background: #fff;
  border: 1px solid var(--ds-border-light, #e5e5e5);
  border-radius: var(--ds-radius-md, 8px);
  font-size: var(--ds-text-xs, 12px);
  color: var(--ds-text-strong, #1a1a1a);
}

.rcadmin__bookings-loading {
  display: flex;
  justify-content: center;
  padding: var(--ds-space-3, 12px) 0;
}

.rcadmin__bookings-error {
  color: rgb(var(--v-theme-error, 211, 47, 47));
  font-size: var(--ds-text-xs, 12px);
}

.rcadmin__bookings-empty {
  color: var(--ds-text-muted);
  font-size: var(--ds-text-xs, 12px);
  text-align: center;
  padding: var(--ds-space-2, 8px) 0;
}

.rcadmin__bookings-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--ds-space-3, 12px);
  padding-bottom: var(--ds-space-3, 12px);
  margin-bottom: var(--ds-space-2, 8px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.rcadmin__bookings-stat-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark, #3F998F);
  line-height: 1.2;
}

.rcadmin__bookings-stat-label {
  margin-top: 2px;
  font-size: 11px;
  color: var(--ds-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.rcadmin__bookings-restaurant {
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.rcadmin__bookings-restaurant:last-child {
  border-bottom: none;
}

.rcadmin__bookings-restaurant-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2, 8px);
  width: 100%;
  padding: 4px 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  color: inherit;
  border-radius: 4px;
  transition: background 150ms ease;
}

.rcadmin__bookings-restaurant-header:hover,
.rcadmin__bookings-restaurant-header:focus-visible {
  background: var(--ds-brand-primary-light, #e9f4f3);
  outline: none;
}

.rcadmin__bookings-restaurant-info {
  flex: 1;
  min-width: 0;
}

.rcadmin__bookings-restaurant-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-strong, #1a1a1a);
  word-break: break-word;
}

.rcadmin__bookings-restaurant-market {
  font-size: 11px;
  color: var(--ds-text-muted);
  margin-top: 1px;
}

.rcadmin__bookings-restaurant-totals {
  text-align: right;
  flex-shrink: 0;
  min-width: 80px;
}

.rcadmin__bookings-restaurant-count {
  font-size: 12px;
  color: var(--ds-text-strong, #1a1a1a);
}

.rcadmin__bookings-restaurant-spend {
  font-size: 11px;
  color: var(--ds-text-muted);
  margin-top: 1px;
}

.rcadmin__bookings-restaurant-chevron {
  color: var(--ds-text-muted);
  flex-shrink: 0;
}

.rcadmin__bookings-rows {
  padding: 4px 0 4px 4px;
}

.rcadmin__bookings-row {
  display: grid;
  grid-template-columns: 70px 1fr 70px;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.rcadmin__bookings-row:last-child {
  border-bottom: none;
}

.rcadmin__bookings-row-date {
  color: var(--ds-text-muted);
}

.rcadmin__bookings-row-party {
  text-align: center;
  color: var(--ds-text-strong, #1a1a1a);
}

.rcadmin__bookings-row-spend {
  text-align: right;
  color: var(--ds-text-strong, #1a1a1a);
}

/* ─── Alerts (messages, declines) ─────────────────────────────────────── */

.rcadmin__alert {
  margin: 0;
  width: 100%;
  font-size: var(--ds-text-sm);
}

.rcadmin__alert :deep(.v-alert__content) {
  font-size: var(--ds-text-sm);
}

.rcadmin__alert-body {
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.5;
}

.rcadmin__decline-chip {
  margin-right: var(--ds-space-2, 8px);
  font-weight: 500;
}

/* ─── Footer ──────────────────────────────────────────────────────────── */

.rcadmin__footer {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-space-2, 8px);
  padding-top: var(--ds-space-2, 8px);
  border-top: 1px solid var(--ds-border-subtle);
}

.rcadmin__footer-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: var(--ds-brand-primary-light);
  color: var(--ds-brand-primary-darker);
  border-radius: 999px;
  font-size: var(--ds-text-sm);
  font-weight: 600;
  text-decoration: none;
  transition: background-color 150ms ease;
}

.rcadmin__footer-link:hover {
  background: var(--ds-brand-primary);
  color: var(--ds-text-on-dark);
}

/* ─── Narrow screens ──────────────────────────────────────────────────── */

@media (max-width: 520px) {
  .rcadmin__header {
    flex-direction: column;
  }

  .rcadmin__actions {
    align-self: flex-end;
    order: -1;
  }
}
.rcadmin__inquiry {
  margin-top: 6px;
}
.rcadmin__inquiry-line {
  font-size: 13px;
  color: var(--ds-text-body);
  line-height: 1.5;
}
.rcadmin__inquiry-error {
  color: #b3261e;
}
.rcadmin__approve {
  margin-top: 6px;
}
.rcadmin__approve-btn {
  margin-top: 6px;
  text-transform: none;
}
</style>
